import { Box, Stack, styled, Typography } from '@mui/material';
import { ButtonCustom } from '../../components/elements/ButtonCustom';
import React, { useMemo } from 'react';
import { StyledParams } from 'types/common';
import { useInjectRoomLevelIssuesPage } from './hook';
import { InputText } from '../../components/elements/InputText';
import { InputSelect } from '../../components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { Controller } from 'react-hook-form';
import { DatePicker } from '../../components/elements/DatePicker';
import dayjs from 'dayjs';
import { dropdownValueTypes } from 'const/option';
import { lowerCase, startCase } from 'lodash';
import { getNameOption } from 'utils/convertData';
import EditIcon from '@mui/icons-material/Edit';
import { useRoomContext } from 'contexts/RoomContext';

export const RoomLevelIssuesPage = () => {
  const {
    handleSubmit,
    register,
    control,
    setValue,
    watch,
    errors,
    options,
    roomIssues,
    statusOptions,
    handleSubmitForm,
    onDelete,
    isArchived,
    inputRef,
    onChangePhoto
  } = useInjectRoomLevelIssuesPage();
  const { users } = useRoomContext();

  const roomLevelIssueOption = useMemo(() => {
    const newOptions = options?.filter((item) =>
      item.options.some((option) => option.type === dropdownValueTypes.roomLevelIssue)
    );
    return newOptions;
  }, [options]);

  const newFile = watch('file');

  return (
    <WrapperCreatePage>
      <Stack
        width={'100%'}
        flexDirection={'row'}
        alignItems={'center'}
        justifyContent={'space-between'}>
        <TitlePageStyled>Location Level Issues</TitlePageStyled>
        <ButtonCustom
          sx={{ width: '120px', height: '46px', padding: '0 20px' }}
          label={'Back'}
          onClick={() => window.history.go(-1)}
        />
      </Stack>

      <form style={{ marginTop: 24, width: 600 }} onSubmit={handleSubmit(handleSubmitForm)}>
        <Stack gap={3}>
          <Stack gap={1}>
            <Typography fontWeight={400} mb={1.25} color={'#818181'}>
              Photo
            </Typography>
            {(roomIssues?.photo || newFile) && (
              <PreviewImage>
                <Box
                  component="img"
                  alt=""
                  src={newFile ? URL.createObjectURL(newFile) : roomIssues?.photo?.url}
                  sx={{ width: 185, display: 'block', objectFit: 'contain' }}
                />
                <BtnEdit onClick={() => inputRef.current?.click()} />
                <input
                  type="file"
                  style={{ display: 'none' }}
                  ref={inputRef}
                  onChange={onChangePhoto}
                />
              </PreviewImage>
            )}
          </Stack>
          {roomLevelIssueOption?.map((option) => (
            <React.Fragment key={option.name}>
              <Controller
                control={control}
                render={({ field: { onChange, value } }) => (
                  <InputSelect
                    inputError={errors?.[`option_${option.id}`]?.message as string}
                    title={`${startCase(lowerCase(getNameOption(option)))}:`}
                    value={value}
                    placeholder={`Select ${startCase(lowerCase(getNameOption(option)))}`}
                    onChange={onChange}
                    sx={{ padding: 0 }}
                    options={option?.dropdownValues?.map((item) => ({
                      label: item.value,
                      value: item.value
                    }))}
                  />
                )}
                name={`option_${option.id}`}
              />
              {watch(`option_${option.id}`) === 'OTHER' && (
                <InputText
                  inputError={errors?.[`option_${option.id}_name`]?.message}
                  placeholder={`Enter ${startCase(lowerCase(getNameOption(option)))} Name`}
                  title={`${startCase(lowerCase(getNameOption(option)))} Name:`}
                  inputProps={{
                    ...register(`option_${option.id}_name`, { required: true })
                  }}
                />
              )}
            </React.Fragment>
          ))}

          <InputText
            title="Description"
            placeholder="Description"
            inputProps={{
              ...register('description'),
              multiline: true,
              rows: 5,
              style: { padding: '0px' }
            }}
          />
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSelect
                title={'Validation'}
                inputError={errors.validated?.message || ''}
                value={value}
                placeholder={'Validation'}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  onChange(e.target.value);
                }}
                options={[
                  { value: 'Yes', label: 'Yes' },
                  { value: 'No', label: 'No' }
                ]}
              />
            )}
            name={`validated`}
          />
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSelect
                onChange={onChange}
                inputError={errors.actionBy?.message || ''}
                title={'Action By'}
                value={value}
                placeholder={'Select a user'}
                options={[
                  ...(users?.map((item) => ({
                    label: item.label,
                    value: item.label
                  })) ?? []),
                  { label: 'Other', value: 'OTHER' }
                ]}
              />
            )}
            name={`actionBy`}
          />
          {watch('actionBy') === 'OTHER' && (
            <InputText
              title={'Action By Name'}
              inputError={errors.actionByName?.message}
              placeholder={'Enter Action By Name'}
              inputProps={{
                ...register('actionByName')
              }}
            />
          )}
          <Stack gap={'10px'}>
            <Controller
              control={control}
              render={({ field: { value } }) => (
                <DatePicker
                  inputError={errors.actionBy?.message || ''}
                  style={{ flexDirection: 'column' }}
                  title={'Choose Action By Date'}
                  onChange={(val) => setValue('actionByDate', val ? dayjs(val).toISOString() : '')}
                  value={value ? dayjs(value) : undefined}
                />
              )}
              name={'actionByDate'}
            />
          </Stack>
          <Controller
            control={control}
            render={({ field: { onChange, value } }) => (
              <InputSelect
                title={'Status'}
                inputError={errors.status?.message || ''}
                value={value}
                placeholder={'Status'}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  onChange(e.target.value);
                  if (e.target.value === 'RESOLVED') {
                    setValue('resolutionDate', dayjs().toISOString());
                  }
                }}
                options={statusOptions}
              />
            )}
            name={`status`}
          />
          {watch('status') === 'RESOLVED' && (
            <Stack style={{ width: '100%' }}>
              <Controller
                control={control}
                render={({ field: { value } }) => (
                  <DatePicker
                    inputError={errors?.resolutionDate?.message}
                    style={{ flexDirection: 'column' }}
                    title={'Choose Resolution Date'}
                    onChange={(val) => {
                      const date = dayjs(val);
                      if (!val || !date.isValid()) {
                        setValue('resolutionDate', '');
                      } else {
                        setValue('resolutionDate', date.toISOString());
                      }
                    }}
                    value={value ? dayjs(value) : undefined}
                  />
                )}
                name={'resolutionDate'}
              />
            </Stack>
          )}
          <FormFooter>
            <ButtonCustom style={{ width: 140 }} label="Save" type="submit" />
            <ButtonCustom
              style={{ width: 140 }}
              label={isArchived ? 'Unarchive' : 'Archive'}
              onClick={onDelete}
              type="button"
            />
          </FormFooter>
        </Stack>
      </form>
    </WrapperCreatePage>
  );
};
const WrapperCreatePage = styled(Stack)(() => ({
  padding: '40px 30px 50px 40px',
  width: '100%',
  minHeight: '100vh',
  gap: '20px'
}));
const TitlePageStyled = styled(Typography)(({ theme }: StyledParams) => ({
  margin: '20px 0',
  color: theme.colors.base,
  fontSize: '24px',
  fontWeight: 900,
  fontFamily: `'Avenir',sans-serif`
}));

const FormFooter = styled(Box)(() => ({
  display: 'flex',
  gap: 20
}));

const PreviewImage = styled(Box)({
  position: 'relative',
  width: '185px',
  borderRadius: 10,
  overflow: 'hidden',
  '&:hover svg': {
    display: 'block'
  }
});

const BtnEdit = styled(EditIcon)({
  position: 'absolute',
  top: 0,
  right: 0,
  color: 'white',
  zIndex: '9999',
  cursor: 'pointer',
  display: 'none',
  background: 'linear-gradient(rgba(50, 50, 93, 0.25), rgba(0, 0, 0, 0.3))',
  boxShadow: 'rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px'
});
